import { inject, Injectable } from '@angular/core';
import { GenericStore } from '@app/stores/generic.store';
import { TranslateService } from '@ngx-translate/core';
import { MasterdataService } from '../masterdata.service';
import { ToastService } from '@app/services/toast.service';
import { Router } from '@angular/router';
import {
  DatabaseManagementFormGroup,
  MergeLanguagesWithoutType,
} from '@app/models/form.models';
import { FormControl, FormGroup } from '@angular/forms';
import {
  DatabaseManagement,
  EntityKey,
  SaveContext,
} from '@app/models/admin.model';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class DatabaseSaveService {
  private genericStore = inject(GenericStore);
  private translate = inject(TranslateService);

  constructor(
    private masterdata: MasterdataService,
    private toast: ToastService,
    private router: Router
  ) {}

  private registry(entity: EntityKey) {
    switch (entity) {
      case 'country':
        return {
          create: (payload: DatabaseManagement) =>
            this.masterdata.createCountry(payload),
          update: (payload: DatabaseManagement) =>
            this.masterdata.updateCountry(payload),
          routeBase: '/admin/database/country',
          databaseSelector: 'country',
        };
      case 'currency':
        return {
          create: (payload: DatabaseManagement) =>
            this.masterdata.createCurrency(payload),
          update: (payload: DatabaseManagement) =>
            this.masterdata.updateCurrency(payload),
          routeBase: '/admin/database/currency',
          databaseSelector: 'currency',
        };
      case 'legalEntityRole':
        return {
          create: (payload: DatabaseManagement) =>
            this.masterdata.createLegalEntityRole(payload),
          update: (payload: DatabaseManagement) =>
            this.masterdata.updateLegalEntityRole(payload),
          routeBase: '/admin/database/legalEntityRole',
          databaseSelector: 'legalEntityRole',
        };
      case 'nameTitle':
        return {
          create: (payload: DatabaseManagement) =>
            this.masterdata.createNameTitle(payload),
          update: (payload: DatabaseManagement) =>
            this.masterdata.updateNameTitle(payload),
          routeBase: '/admin/database/nameTitle',
          databaseSelector: 'nameTitle',
        };
      case 'personRole':
        return {
          create: (payload: DatabaseManagement) =>
            this.masterdata.createPersonRole(payload),
          update: (payload: DatabaseManagement) =>
            this.masterdata.updatePersonRole(payload),
          routeBase: '/admin/database/personRole',
          databaseSelector: 'personRole',
        };
      case 'projectClassification':
        return {
          create: (payload: DatabaseManagement) =>
            this.masterdata.createProjectClassification(payload),
          update: (payload: DatabaseManagement) =>
            this.masterdata.updateProjectClassification(payload),
          routeBase: '/admin/database/projectClassification',
          databaseSelector: 'projectClassification',
        };
    }
  }

  saveFromForms(
    form: FormGroup<DatabaseManagementFormGroup>,
    context: SaveContext,
    original?: DatabaseManagement | null
  ): void {
    const { entity } = context;
    const reg = this.registry(entity);

    const detailsGroup = form.get('details') as FormGroup<{
      code: FormControl<string>;
      description: FormControl<string | null>;
    }>;

    const details =
      detailsGroup.getRawValue() ??
      ({ code: '', description: null } as {
        code: string;
        description: string | null;
      });

    const localized = this.buildLocalizedFromTab(
      form.controls.localizationsTab,
      'localizedName'
    );

    const payload: DatabaseManagement = {
      ...(original ?? ({} as DatabaseManagement)),
      code: String(details.code ?? '').trim(),
      description: (details.description ?? '')?.toString().trim() || null,
      isActive: original?.isActive ?? true,
      localized,
    };

    const req$ = original ? reg.update(payload) : reg.create(payload);

    req$.subscribe({
      next: (res: any) => {
        if (original) {
          this.toast.success(
            this.translate.instant(
              `admin.${reg.databaseSelector}.toasts.update`
            )
          );
        } else {
          this.toast.success(
            this.translate.instant(
              `admin.${reg.databaseSelector}.toasts.create`
            )
          );
          const id = res?.data?.id ?? res?.id;
          if (id)
            this.router.navigate([
              `/admin/database/${reg.databaseSelector}`,
              id,
            ]);
        }
      },
      error: (err: HttpErrorResponse) => {
        const msg =
          err?.error?.error?.message || err?.error?.message || err?.message;
        this.toast.error(msg);
      },
    });
  }

  private buildLocalizedFromTab(
    tabForm:
      | FormGroup<{ [language: string]: FormGroup<MergeLanguagesWithoutType> }>
      | null
      | undefined,
    pick: 'localizedName'
  ): Record<string, string> {
    const out: Record<string, string> = {};
    if (!tabForm) return out;

    const langMap = this.genericStore.languageEntityMap();

    Object.entries(tabForm.controls).forEach(([code, group]) => {
      const langKey = langMap[code]?.description;
      if (!langKey) return;

      const val = (group.controls[pick]?.value ?? '').trim();
      if (val) out[langKey] = val;
    });

    return out;
  }
}
